import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { aplicarAtualizacao, useNovaVersao } from '../lib/atualizacao.js';
import { arquivos, db, sincronizarAgora, useSincronizando } from '../lib/local.js';
import { Botao, Menu } from './ui/index.js';

/**
 * Indicador permanente de pendencias, no topo.
 *
 * Nao e enfeite: e a mitigacao do risco numero um do projeto. A equipe precisa
 * ver, sem procurar, quantas fichas ainda nao sairam do tablet — antes de sair
 * do haras, e nao depois. Compacto quando esta tudo certo; em ambar, com a
 * contagem, quando ha algo por enviar. O detalhe fica no menu.
 */
export function IndicadorSincronia({
  persistente,
  naLateral = false,
}: {
  persistente: boolean;
  /** No rodape da barra lateral: texto sempre a vista e menu abrindo para cima. */
  naLateral?: boolean;
}) {
  const pendentes = useLiveQuery(() => db.outbox.count(), [], 0);
  const emQuarentena = useLiveQuery(() => db.quarantine.count(), [], 0);
  const arquivosPorEnviar = useLiveQuery(async () => (await arquivos.pendentes()).length, [], 0);
  const novaVersao = useNovaVersao();
  const rodando = useSincronizando();
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const ligou = () => setOnline(true);
    const caiu = () => setOnline(false);
    window.addEventListener('online', ligou);
    window.addEventListener('offline', caiu);
    return () => {
      window.removeEventListener('online', ligou);
      window.removeEventListener('offline', caiu);
    };
  }, []);

  const problema = emQuarentena > 0;
  const atencao = pendentes > 0 || arquivosPorEnviar > 0 || !persistente;
  const resumo = problema
    ? `${emQuarentena} em quarentena`
    : pendentes > 0
      ? `${pendentes} por enviar`
      : arquivosPorEnviar > 0
        ? `${arquivosPorEnviar} arquivo(s) por enviar`
      : !online
        ? 'Offline'
        : rodando
          ? 'Sincronizando…'
          : // O texto acompanha a cor do ponto: ambar nunca diz "tudo certo".
            persistente
            ? novaVersao
              ? 'Nova versão'
              : 'Sincronizado'
            : 'Armazenamento sem garantia';
  const corPonto = problema ? 'bg-topo-erro' : atencao ? 'bg-topo-aviso' : online ? 'bg-topo-ok' : 'bg-nevoa/60';

  return (
    <Menu
      rotulo={`Sincronização: ${resumo}`}
      abrir={naLateral ? 'cima' : 'baixo'}
      alinhar={naLateral ? 'esquerda' : 'direita'}
      classeGatilho={
        naLateral
          ? 'flex w-full items-center gap-2.5 rounded-(--radius-controle) px-3 text-[13px] text-lateral-apagado hover:bg-lateral-hover hover:text-sobre-lateral'
          : 'flex items-center gap-2 rounded-(--radius-controle) px-2 text-[13px] text-nevoa hover:bg-gelo/8 hover:text-gelo'
      }
      gatilho={
        <>
          <span aria-hidden className={`size-2 shrink-0 rounded-full ${corPonto}`} />
          <span className={naLateral ? 'truncate' : 'hidden sm:inline'}>{resumo}</span>
        </>
      }
    >
      {(fechar) => (
        <dl className="w-72 space-y-2 px-3 py-2 text-sm">
          <div className="flex justify-between gap-6">
            <dt className="text-apagado">Conexão</dt>
            <dd className="font-medium">{online ? 'Conectado' : 'Offline'}</dd>
          </div>
          <div className="flex justify-between gap-6">
            <dt className="text-apagado">Registros por enviar</dt>
            <dd className={`numero font-medium ${pendentes > 0 ? 'text-aviso' : ''}`}>{pendentes}</dd>
          </div>
          <div className="flex justify-between gap-6">
            <dt className="text-apagado">Fotos e vídeos por enviar</dt>
            <dd className={`numero font-medium ${arquivosPorEnviar > 0 ? 'text-aviso' : ''}`}>{arquivosPorEnviar}</dd>
          </div>
          <div className="flex justify-between gap-6">
            <dt className="text-apagado">Em quarentena</dt>
            <dd className={`numero font-medium ${problema ? 'text-erro' : ''}`}>{emQuarentena}</dd>
          </div>
          <div className="flex justify-between gap-6">
            <dt className="text-apagado">Armazenamento</dt>
            <dd className={`font-medium whitespace-nowrap ${persistente ? '' : 'text-aviso'}`}>{persistente ? 'Garantido' : 'Sem garantia'}</dd>
          </div>
          {!persistente && (
            <p className="border-t border-borda pt-2 text-xs text-apagado">
              Instale o app na tela de início para o navegador não apagar os dados guardados no aparelho.
            </p>
          )}
          {problema && (
            <p className="border-t border-borda pt-2 text-xs text-erro">
              Há registros que o servidor recusou. Eles precisam de uma decisão antes de sair do haras.
            </p>
          )}
          {novaVersao && (
            <div className="space-y-2 border-t border-borda pt-2 text-xs">
              <p className="font-medium">Nova versão do app disponível.</p>
              {pendentes + arquivosPorEnviar > 0 ? (
                <p className="text-apagado">
                  Ela é instalada depois que os {pendentes + arquivosPorEnviar} registro(s) e arquivo(s) deste aparelho forem
                  enviados — trocar de versão com coleta pendente arrisca o dado.
                </p>
              ) : (
                <Botao tamanho="sm" variante="principal" onClick={() => void aplicarAtualizacao()}>
                  Atualizar agora
                </Botao>
              )}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 border-t border-borda pt-2">
            <Botao tamanho="sm" carregando={rodando} disabled={!online} onClick={() => void sincronizarAgora().catch(() => undefined)}>
              Sincronizar agora
            </Botao>
            <Link to="/sincronizacao" onClick={fechar} className="text-xs font-medium text-destaque-texto hover:underline">
              Ver detalhes
            </Link>
          </div>
        </dl>
      )}
    </Menu>
  );
}
